import { Crown, LogOut, User as UserIcon, Sparkles, Zap } from "lucide-react";

import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import { useSession } from "@/contexts/SessionContext";

const TIER_META = {
    trial: { label: "Trial", color: "#8A93A6", Icon: Sparkles },
    creator: { label: "Creator", color: "#2F58CD", Icon: Zap },
    pro: { label: "Pro", color: "#00F5D4", Icon: Crown },
    expired: { label: "Expired", color: "#FF5470", Icon: Sparkles },
};

export default function UserAvatarMenu() {
    const { user, tier, signOut, openSubscriptionDrawer } = useSession();
    if (!user) return null;
    const meta = TIER_META[tier] || TIER_META.trial;
    const initial = (user.name || user.email || "?").charAt(0).toUpperCase();

    return (
        <Popover>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    className="group relative flex h-9 w-9 items-center justify-center rounded-full border border-[#1F2A40] bg-[#111726] text-sm font-medium text-[#E8ECF5] transition-colors hover:border-[#00F5D4]/40"
                    aria-label="Account menu"
                    data-testid="user-avatar-trigger"
                >
                    {user.picture ? (
                        <img
                            src={user.picture}
                            alt={user.name || "avatar"}
                            className="h-full w-full rounded-full object-cover"
                        />
                    ) : (
                        <span>{initial}</span>
                    )}
                    <span
                        className="absolute -bottom-1 -right-1 inline-flex h-3.5 w-3.5 items-center justify-center rounded-full border border-[#0B0F19] bg-[#0B0F19]"
                        style={{ color: meta.color }}
                        data-testid="user-avatar-tier-dot"
                    >
                        <meta.Icon className="h-2.5 w-2.5" />
                    </span>
                </button>
            </PopoverTrigger>
            <PopoverContent
                align="end"
                className="z-50 w-[260px] border-[#1F2A40] bg-[#111726] p-3 text-[#E8ECF5]"
                data-testid="user-avatar-menu"
            >
                <div className="flex flex-col gap-3">
                    <div className="flex items-center gap-3">
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-[#1F2A40] bg-[#0B0F19] text-sm">
                            {user.picture ? (
                                <img
                                    src={user.picture}
                                    alt=""
                                    className="h-full w-full rounded-full object-cover"
                                />
                            ) : (
                                <UserIcon className="h-4 w-4 text-[#8A93A6]" />
                            )}
                        </div>
                        <div className="flex min-w-0 flex-col">
                            <span
                                className="truncate text-sm font-medium text-[#E8ECF5]"
                                data-testid="user-name"
                            >
                                {user.name || "—"}
                            </span>
                            <span
                                className="truncate font-mono text-[10px] text-[#8A93A6]"
                                data-testid="user-email"
                            >
                                {user.email}
                            </span>
                        </div>
                    </div>
                    <div
                        className="flex items-center justify-between rounded-md border border-[#1F2A40] bg-[#0B0F19] px-2.5 py-1.5"
                        data-testid="user-tier-row"
                    >
                        <span className="inline-flex items-center gap-2 text-xs">
                            <meta.Icon
                                className="h-3.5 w-3.5"
                                style={{ color: meta.color }}
                            />
                            <span
                                className="font-mono uppercase tracking-[0.22em]"
                                style={{ color: meta.color }}
                            >
                                {meta.label}
                            </span>
                        </span>
                        <button
                            type="button"
                            onClick={() => openSubscriptionDrawer()}
                            className="font-mono text-[10px] uppercase tracking-wider text-[#00F5D4] transition-colors hover:underline"
                            data-testid="menu-subscription"
                        >
                            Subscription
                        </button>
                    </div>
                    <button
                        type="button"
                        onClick={signOut}
                        className="inline-flex w-full items-center justify-between rounded-md px-2.5 py-1.5 text-sm text-[#8A93A6] transition-colors hover:bg-[#1A2235] hover:text-[#E8ECF5]"
                        data-testid="menu-signout"
                    >
                        <span className="inline-flex items-center gap-2">
                            <LogOut className="h-3.5 w-3.5" />
                            Sign out
                        </span>
                    </button>
                </div>
            </PopoverContent>
        </Popover>
    );
}
